import { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import { useAuth } from '@context/AuthContext'
import OPList from './components/OPList'
import OPFormModal from './components/OPFormModal'
import DeclaracionesPanel from './components/DeclaracionesPanel'
import styles from './CuentaCompensacion.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

export async function authHeaders() {
    const session = await fetchAuthSession()
    return { Authorization: `Bearer ${session.tokens.idToken.toString()}` }
}

function CuentaCompensacion() {
    const { userEmpresa, isAdmin, isSuperAdmin } = useAuth()
    const canManage = isAdmin || isSuperAdmin
    const empresaId = userEmpresa

    const [ops, setOps] = useState([])
    const [selectedOP, setSelectedOP] = useState(null)
    const [showOPModal, setShowOPModal] = useState(false)
    const [editingOP, setEditingOP] = useState(null)
    const [loading, setLoading] = useState(true)
    const [msg, setMsg] = useState(null)

    const loadOPs = useCallback(async () => {
        if (!empresaId || empresaId === 'Sin empresa') return
        setLoading(true)
        try {
            const headers = await authHeaders()
            const res = await axios.get(`${API}/cuenta-compensacion/${empresaId}/ops`, { headers })
            setOps(res.data.ops || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando órdenes de pedido: ' + (err.response?.data?.error || err.message) })
        } finally {
            setLoading(false)
        }
    }, [empresaId])

    useEffect(() => { loadOPs() }, [loadOPs])

    // Si la OP seleccionada fue modificada, actualizar referencia
    useEffect(() => {
        if (selectedOP) {
            const updated = ops.find(o => o.op_id === selectedOP.op_id)
            if (updated) setSelectedOP(updated)
        }
    }, [ops])  // eslint-disable-line react-hooks/exhaustive-deps

    const handleNewOP = () => {
        setEditingOP(null)
        setShowOPModal(true)
    }

    const handleEditOP = (op) => {
        setEditingOP(op)
        setShowOPModal(true)
    }

    const handleDeleteOP = async (op) => {
        if (!isSuperAdmin) {
            setMsg({ type: 'error', text: 'Solo SuperAdmins pueden eliminar OPs' })
            return
        }
        if (!window.confirm(`¿Eliminar la OP "${op.op_numero}"? Esta acción también eliminará todas sus declaraciones de importación.`)) return
        try {
            const headers = await authHeaders()
            await axios.delete(`${API}/cuenta-compensacion/${empresaId}/ops/${op.op_id}`, { headers })
            setMsg({ type: 'success', text: 'OP eliminada correctamente' })
            if (selectedOP?.op_id === op.op_id) setSelectedOP(null)
            loadOPs()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error eliminando OP: ' + (err.response?.data?.error || err.message) })
        }
    }

    const handleOPSaved = () => {
        setShowOPModal(false)
        setMsg({ type: 'success', text: editingOP ? 'OP actualizada' : 'OP creada exitosamente' })
        loadOPs()
    }

    const totalOPs   = ops.length
    const opsActivas = ops.filter(o => o.estado === 'activa').length

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIcon}>🌐</div>
                        <div>
                            <h1 className={styles.title}>Cuenta de Compensación</h1>
                            <p className={styles.subtitle}>
                                Gestión de órdenes de pedido, facturas de comercio exterior y declaraciones de importación (DIM)
                            </p>
                        </div>
                    </div>

                    {canManage && (
                        <button className={styles.btnNew} onClick={handleNewOP}>
                            + Nueva OP
                        </button>
                    )}
                </div>

                {/* Stats */}
                <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                        <span className={styles.statNum}>{totalOPs}</span>
                        <span className={styles.statLabel}>Total OPs</span>
                    </div>
                    <div className={`${styles.statCard} ${styles.statActiva}`}>
                        <span className={styles.statNum}>{opsActivas}</span>
                        <span className={styles.statLabel}>OPs activas</span>
                    </div>
                    <div className={`${styles.statCard} ${styles.statCerrada}`}>
                        <span className={styles.statNum}>{totalOPs - opsActivas}</span>
                        <span className={styles.statLabel}>OPs cerradas</span>
                    </div>
                </div>
            </div>

            {/* ── Alertas ── */}
            {msg && (
                <div className={`${styles.alert} ${styles[`alert${msg.type.charAt(0).toUpperCase() + msg.type.slice(1)}`]}`}>
                    {msg.text}
                    <button className={styles.alertClose} onClick={() => setMsg(null)}>✕</button>
                </div>
            )}

            {/* ── Contenido ── */}
            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loadingBlock}>
                        <div className={styles.spinner}></div>
                        <span>Cargando órdenes de pedido...</span>
                    </div>
                ) : empresaId === 'Sin empresa' ? (
                    <div className={styles.emptyState}>
                        <span>🏢</span>
                        <h3>Sin empresa asignada</h3>
                        <p>Tu usuario aún no tiene empresa asignada. Contacta al administrador.</p>
                    </div>
                ) : (
                    <div className={styles.layout}>
                        {/* Lista de OPs */}
                        <div className={`${styles.listPanel} ${selectedOP ? styles.listPanelNarrow : ''}`}>
                            <OPList
                                ops={ops}
                                selectedOP={selectedOP}
                                canManage={canManage}
                                canDelete={isSuperAdmin}
                                onSelect={setSelectedOP}
                                onEdit={handleEditOP}
                                onDelete={handleDeleteOP}
                                onNew={handleNewOP}
                            />
                        </div>

                        {/* Panel de declaraciones */}
                        {selectedOP && (
                            <div className={styles.detailPanel}>
                                <DeclaracionesPanel
                                    empresaId={empresaId}
                                    op={selectedOP}
                                    canManage={canManage}
                                    onClose={() => setSelectedOP(null)}
                                    setMsg={setMsg}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal Nueva/Editar OP */}
            {showOPModal && (
                <OPFormModal
                    empresaId={empresaId}
                    op={editingOP}
                    onSaved={handleOPSaved}
                    onClose={() => setShowOPModal(false)}
                    setMsg={setMsg}
                    canChangeStatus={isSuperAdmin}
                />
            )}
        </div>
    )
}

export default CuentaCompensacion
